import type React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  FormHelperText,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../../store";
import type { AppDispatch } from "../../store";
import { addTruckHistory } from "../../store/features/truckHistorySlice";
import type translations from "../../translations/translations.json";
import { toastEmitter } from "../../utils/toastEmitter";
import type { Truck } from "../../types/truck";
import { TruckStatus } from "../../types/truck";
import { getAvailableNextStatuses } from "../../utils/truckStatusFlow";

type TranslationKeys = keyof typeof translations.en;

interface AddTruckHistoryFormProps {
  open: boolean;
  onClose: () => void;
  truckId: string;
  onSuccess: () => void;
  onTruckUpdate?: (updatedTruck: Truck) => void;
  trucks: Truck[];
}

// Filter out RETIRED status from available statuses
const TRUCK_STATUSES = Object.values(TruckStatus).filter(
  (status) => status !== TruckStatus.RETIRED,
);

const AddTruckHistoryForm: React.FC<AddTruckHistoryFormProps> = ({
  open,
  onClose,
  truckId,
  onSuccess,
  onTruckUpdate,
  trucks,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    cityId: "",
    destinationCityId: "",
    status: "",
    notes: "",
    timestamp: new Date(),
  });
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { cities } = useSelector((state: RootState) => state.cities);

  // Get current truck status
  const currentTruck = trucks.find((t) => t._id === truckId);
  const currentStatus = currentTruck?.currentState?.status || TruckStatus.UNKNOWN;
  const isUnretiring = currentTruck?.retired && formData.status !== TruckStatus.RETIRED;

  // Get available next statuses based on current status
  const availableNextStatuses = getAvailableNextStatuses(currentStatus);

  const isDestinationRequired =
    formData.status === TruckStatus.MOB || formData.status === TruckStatus.ON_WORK;

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      const targetLocation =
        currentTruck?.currentState?.destination?.cityId ||
        currentTruck?.currentState?.location?.cityId ||
        "";
      setFormData({
        cityId: targetLocation,
        destinationCityId: "",
        status: "",
        notes: "",
        timestamp: new Date(),
      });
    }
  }, [open, currentTruck]);

  // Get available locations based on status
  const getAvailableLocations = () => {
    const targetLocation =
      currentTruck?.currentState?.destination?.cityId ||
      currentTruck?.currentState?.location?.cityId;

    if (targetLocation) {
      return cities.filter((city) => city._id === targetLocation);
    }
    return cities;
  };

  // Handle status change
  const handleStatusChange = (newStatus: string) => {
    setFormData((prev) => ({
      ...prev,
      status: newStatus,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate location change
    if (
      !isUnretiring &&
      (formData.status === TruckStatus.ON_WORK ||
        formData.status === TruckStatus.MOB ||
        formData.status === TruckStatus.MAINTENANCE)
    ) {
      const targetLocation =
        currentTruck?.currentState?.destination?.cityId ||
        currentTruck?.currentState?.location?.cityId;
      if (targetLocation && formData.cityId !== targetLocation) {
        setError(
          t(
            "Location can only be changed to the current destination or location" as TranslationKeys,
          ),
        );
        setLoading(false);
        return;
      }
    }

    try {
      const historyData: any = {
        location: {
          cityId: formData.cityId,
        },
        status: formData.status,
        notes: formData.notes || undefined,
        timestamp: formData.timestamp.toISOString(),
      };

      // Add destination for MOB and ON_WORK statuses
      if (
        formData.status === TruckStatus.MOB ||
        formData.status === TruckStatus.ON_WORK ||
        formData.status === TruckStatus.MAINTENANCE
      ) {
        historyData.destination = {
          cityId: formData.destinationCityId,
        };
      }

      const result = await dispatch(
        addTruckHistory({
          truckId,
          historyData,
        }),
      ).unwrap();

      // Update the truck state locally if callback is provided
      if (onTruckUpdate && result.truck) {
        onTruckUpdate(result.truck);
      }

      toastEmitter.emit(t("Truck history added successfully" as TranslationKeys), "success");
      onSuccess();
      onClose();
      setFormData({
        cityId: "",
        destinationCityId: "",
        status: "",
        notes: "",
        timestamp: new Date(),
      });
    } catch (err: any) {
      setError(err.message || t("Failed to add truck history" as TranslationKeys));
    } finally {
      setLoading(false);
    }
  };

  // Check if location should be disabled
  const shouldDisableLocation = Boolean(
    currentTruck?.currentState?.destination?.cityId || currentTruck?.currentState?.location?.cityId,
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t("Add Truck History" as TranslationKeys)}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t("Status" as TranslationKeys)}</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              required
              label={t("Status" as TranslationKeys)}
            >
              {availableNextStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {t(status as TranslationKeys)}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {t("Current status" as TranslationKeys)}: {t(currentStatus as TranslationKeys)}
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <Autocomplete
              value={cities.find((city) => city._id === formData.cityId) || null}
              onChange={(_, newValue) => {
                setFormData({ ...formData, cityId: newValue?._id || "" });
              }}
              options={getAvailableLocations()}
              getOptionLabel={(option) => option.name}
              disabled={shouldDisableLocation}
              renderInput={(params) => (
                <TextField {...params} label={t("Location" as TranslationKeys)} required />
              )}
            />
            {shouldDisableLocation && (
              <FormHelperText>
                {t("Location must match current destination or location" as TranslationKeys)}
              </FormHelperText>
            )}
          </FormControl>

          {isDestinationRequired && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Autocomplete
                value={cities.find((city) => city._id === formData.destinationCityId) || null}
                onChange={(_, newValue) => {
                  setFormData({ ...formData, destinationCityId: newValue?._id || "" });
                }}
                options={cities}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} label={t("Destination" as TranslationKeys)} required />
                )}
              />
            </FormControl>
          )}

          {formData.status === TruckStatus.MAINTENANCE && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Autocomplete
                value={cities.find((city) => city._id === formData.destinationCityId) || null}
                onChange={(_, newValue) => {
                  setFormData({ ...formData, destinationCityId: newValue?._id || "" });
                }}
                options={cities}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} label={t("Destination" as TranslationKeys)} />
                )}
              />
            </FormControl>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              label={t("Notes" as TranslationKeys)}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={3}
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label={t("Timestamp" as TranslationKeys)}
                value={formData.timestamp}
                onChange={(newValue) => {
                  if (newValue) {
                    setFormData({ ...formData, timestamp: newValue });
                  }
                }}
              />
            </LocalizationProvider>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("Cancel" as TranslationKeys)}</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : t("Add" as TranslationKeys)}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddTruckHistoryForm;
